import {
  EPermission,
  ESmartTextResultState,
  SmartTextResultResponse,
} from 'api/core';
import { useGetSmartText, useUpsertSmartTextResult } from 'api/useSmartTextApi';
import { AnimatedIcon } from 'components/Icon/AnimatedIcon';
import { Page } from 'layouts/Admin/Page';
import { CheckCircle2Icon, CircleSlash2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { SmartTextCreate } from './SmartTextCreate';
import { PermissionProtectedComponent } from 'auth/PermissionProtectedComponent';
import { EditableText } from 'components/EditableText';
import { twMerge } from 'tailwind-merge';
import { Brain } from 'lucide-react';
import { SmartTextRegenerate } from './SmartTextRegenerate';
import { useAuth } from 'auth/AuthProvider';

export const SmartText = () => {
  const { id } = useParams<{ id: string }>();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { data } = useGetSmartText(id);

  const { mutateAsync: upsertTextEntryAsync } = useUpsertSmartTextResult();

  const [hasChanges, setHasChanges] = useState(false);

  const { hasPermissions } = useAuth();

  useEffect(() => {
    if (!data?.results.length) {
      setSelectedIndex(null);
      return;
    }

    if (data?.results.length && selectedIndex === null) {
      const index = data.results.findIndex(
        (e) =>
          e.state !== ESmartTextResultState.Pending &&
          e.state !== ESmartTextResultState.Failed
      );
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [data, selectedIndex]);

  if (!data) return null;

  const handleSave = async (
    entry: SmartTextResultResponse,
    newValue: string
  ) => {
    await upsertTextEntryAsync({
      smartTextResultUpsertRequest: {
        smartTextId: data.id,
        smartTextFormatId: entry.format.id,
        state: ESmartTextResultState.UserGenerated,
        text: newValue,
      },
    });
  };

  return (
    <Page
      title={data._case.address}
      breadcrumbs={[{ name: 'Smart Text' }, { name: data._case.address }]}
      alert={{
        header: 'Smart Text resultat',
        message:
          'Her kan du se resultatet af din Smart Text, hvor du manuelt kan gå ind og redigere og tilpasse det efter behov.',
      }}
    >
      <PermissionProtectedComponent permissions={[EPermission.SmartTextWrite]}>
        <SmartTextCreate
          copyOfId={data.id}
          trigger={
            <div className="flex justify-start py-4 w-full">
              <button className="btn btn-primary w-full md:w-auto">
                Opret ny tekst for denne bolig
              </button>
            </div>
          }
        />
      </PermissionProtectedComponent>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Sidebar */}
        <div className="lg:col-span-4">
          <div className="flex flex-col space-y-1 bg-base-200 rounded-xl p-6 shadow-inner">
            {data.results.map((e, i) => {
              const isSelected = selectedIndex === i;
              const isClickable =
                e.state !== ESmartTextResultState.Pending &&
                e.state !== ESmartTextResultState.Failed;

              return (
                <button
                  key={e.id}
                  disabled={!isClickable}
                  onClick={() => isClickable && setSelectedIndex(i)}
                  className={twMerge(
                    'w-full flex items-center justify-between px-4 py-3 text-left rounded-md transition-all group',
                    isClickable
                      ? 'hover:bg-base-100 text-base-content'
                      : 'opacity-50 cursor-not-allowed text-base-content/60'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {/* Dot Indicator */}
                    <span
                      className={twMerge(
                        'w-2 h-2 rounded-full transition-all',
                        isSelected
                          ? 'bg-secondary'
                          : 'bg-transparent group-hover:bg-secondary/40'
                      )}
                    />
                    <span
                      className={twMerge(
                        'transition-colors',
                        isSelected ? 'font-semibold text-base-content' : ''
                      )}
                    >
                      {e.format.name}
                    </span>
                  </div>

                  {/* Status icon */}
                  {e.state === ESmartTextResultState.Pending ? (
                    <AnimatedIcon
                      icon="polygon-icon"
                      autoPlay
                      loop
                      disablePlayOnHover
                      className="h-5 w-5 text-primary"
                    />
                  ) : e.state === ESmartTextResultState.Failed ? (
                    <CircleSlash2Icon className="h-5 w-5 text-error" />
                  ) : (
                    <CheckCircle2Icon className="h-5 w-5 text-success" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Details Panel skeleton if no selection */}
        {selectedIndex === null || !data.results[selectedIndex].text ? (
          <div className="lg:col-span-8 bg-base-200 rounded-xl p-6 pb-4 shadow-inner">
            {selectedIndex === null ? (
              <div className="flex items-center gap-2">
                <span className="skeleton w-1/3 h-6 rounded-md" />
              </div>
            ) : (
              <h2 className="text-xl font-semibold mb-4 text-primary-content">
                {data.results[selectedIndex].format.name}
              </h2>
            )}
            <div className="skeleton w-full h-48 mt-4 rounded-md" />
            <div className="flex justify-between mt-4">
              <div className="skeleton w-1/6 h-6 rounded-md" />
              <div className="skeleton w-1/3 h-6 rounded-md" />
            </div>
          </div>
        ) : null}

        {/* Details Panel */}
        {selectedIndex !== null && data.results[selectedIndex].text ? (
          <div
            className="lg:col-span-8 bg-base-200 rounded-xl p-6 pb-4 shadow-inner"
            key={'result-' + data.results[selectedIndex].id}
          >
            <h2 className="text-xl font-semibold mb-4 text-primary-content">
              {data.results[selectedIndex].format.name}
            </h2>

            <EditableText
              value={data.results[selectedIndex].text}
              onSave={(newValue) =>
                handleSave(data.results[selectedIndex], newValue)
              }
              canCopy={
                data.results[selectedIndex].state !==
                ESmartTextResultState.Pending
              }
              canEdit={
                data.results[selectedIndex].state !==
                  ESmartTextResultState.Pending &&
                hasPermissions([EPermission.SmartTextWrite])
              }
              showCharacterCount={true}
              maxLength={3000}
              onHasChanges={(hasChanges) => {
                setHasChanges(hasChanges);
              }}
              additionalButtons={
                <>
                  <PermissionProtectedComponent
                    permissions={[EPermission.SmartTextWrite]}
                  >
                    {data.results[selectedIndex].state !==
                    ESmartTextResultState.Pending ? (
                      <SmartTextRegenerate
                        smartText={data}
                        smartTextResult={data.results[selectedIndex]}
                        trigger={
                          <button
                            className="btn btn-primary"
                            disabled={hasChanges}
                          >
                            <Brain className="h-4 w-4" />
                            Generer ny tekst
                          </button>
                        }
                      />
                    ) : null}
                  </PermissionProtectedComponent>
                </>
              }
            />
          </div>
        ) : null}
      </div>
    </Page>
  );
};
